// Önizleme görselini ve logosunu telefonda küçültmek (9.32 · D-653).
//
// Görsel şifreli mesajın içinde gidiyor ve bir zarf en çok 64 KB. Sitelerin
// paylaşım görseli genelde 1200×630 ve 100–300 KB; olduğu gibi sığmaz. Bu
// yüzden çözülüp küçültülüp yeniden sıkıştırılıyor.
//
// NEDEN SAF JAVASCRIPT: kurulu uygulamada görsel küçültebilen bir yerel modül
// yok. Yeni bir yerel modül yeni bir mağaza sürümü demek; bu özellik ise
// güncellemeyle (OTA) gidebiliyor. Bedeli hız: büyük bir JPEG'i çözmek eski
// telefonda bir iki saniye sürebilir. Bu iş yalnız gönderen önizleme
// seçtiğinde, bir kez yapılıyor.
//
// Desteklenen: JPEG, PNG ve içinde PNG taşıyan ICO. WebP, GIF ve SVG
// çözülemiyor; o zaman görsel ya da logo olmadan devam edilir.
import { decode as decodeJpegRaw, encode as encodeJpegRaw } from 'jpeg-js';
import { decodePng, encodePng as encodePngRaw } from './png';

export type Rgba = { width: number; height: number; data: Uint8Array };

export type ImageKind = 'jpeg' | 'png' | 'ico' | 'gif' | 'webp' | 'other';

export function sniffImage(bytes: Uint8Array): ImageKind {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
  if (bytes.length >= 4 && bytes[0] === 0 && bytes[1] === 0 && bytes[2] === 1 && bytes[3] === 0) return 'ico';
  if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'gif';
  if (bytes.length >= 12 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'webp';
  return 'other';
}

/** ICO dosyasının içindeki en uygun PNG kaydı (en büyük, ama 256'dan küçük). */
export function pngFromIco(bytes: Uint8Array): Uint8Array | null {
  if (sniffImage(bytes) !== 'ico' || bytes.length < 6) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const count = view.getUint16(4, true);
  let best: { size: number; data: Uint8Array } | null = null;
  for (let i = 0; i < count; i++) {
    const entry = 6 + i * 16;
    if (entry + 16 > bytes.length) break;
    const size = bytes[entry] || 256;
    const length = view.getUint32(entry + 8, true);
    const offset = view.getUint32(entry + 12, true);
    if (offset + length > bytes.length || length < 8) continue;
    const data = bytes.subarray(offset, offset + length);
    if (sniffImage(data) !== 'png') continue; // BMP kayıtları çözülmüyor
    if (!best || (size > best.size && size <= 256)) best = { size, data };
  }
  return best?.data ?? null;
}

/** JPEG, PNG ya da PNG'li ICO'yu RGBA piksele çevir; çözemezse null. */
export function decodeImage(bytes: Uint8Array): Rgba | null {
  try {
    const kind = sniffImage(bytes);
    if (kind === 'jpeg') {
      // Sınırlar kötü niyetli bir dosyanın telefonu kilitlemesini önlüyor.
      const jpeg = decodeJpegRaw(bytes, {
        useTArray: true,
        formatAsRGBA: true,
        maxResolutionInMP: 8,
        maxMemoryUsageInMB: 128,
      });
      return { width: jpeg.width, height: jpeg.height, data: jpeg.data };
    }
    if (kind === 'png') return decodePng(bytes);
    if (kind === 'ico') {
      const png = pngFromIco(bytes);
      return png ? decodePng(png) : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Kaynaktan, ortasından kırparak, tam istenen boyda bir görsel üret. Her hedef
 * piksel, kaynağın ona düşen alanının ortalamasıdır: küçültmede tırtıklanmayı
 * önleyen en basit doğru yöntem.
 */
export function coverResize(src: Rgba, width: number, height: number): Rgba {
  const scale = Math.max(width / src.width, height / src.height);
  const cropW = width / scale;
  const cropH = height / scale;
  const x0 = (src.width - cropW) / 2;
  const y0 = (src.height - cropH) / 2;
  const out = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    const sy0 = Math.floor(y0 + (y * cropH) / height);
    const sy1 = Math.max(sy0 + 1, Math.floor(y0 + ((y + 1) * cropH) / height));
    for (let x = 0; x < width; x++) {
      const sx0 = Math.floor(x0 + (x * cropW) / width);
      const sx1 = Math.max(sx0 + 1, Math.floor(x0 + ((x + 1) * cropW) / width));
      let r = 0; let g = 0; let b = 0; let a = 0; let n = 0;
      for (let sy = sy0; sy < sy1 && sy < src.height; sy++) {
        for (let sx = sx0; sx < sx1 && sx < src.width; sx++) {
          const i = (sy * src.width + sx) * 4;
          const alpha = src.data[i + 3];
          // Saydam pikselin rengi ortalamaya ağırlığı kadar girer; yoksa
          // logonun kenarında koyu bir hale belirirdi.
          r += src.data[i] * alpha; g += src.data[i + 1] * alpha; b += src.data[i + 2] * alpha;
          a += alpha; n++;
        }
      }
      const o = (y * width + x) * 4;
      if (a > 0) {
        out[o] = Math.round(r / a); out[o + 1] = Math.round(g / a); out[o + 2] = Math.round(b / a);
      }
      out[o + 3] = n ? Math.round(a / n) : 0;
    }
  }
  return { width, height, data: out };
}

function flattenOnWhite(src: Rgba): Rgba {
  const out = new Uint8Array(src.data.length);
  for (let i = 0; i < src.data.length; i += 4) {
    const a = src.data[i + 3] / 255;
    out[i] = Math.round(src.data[i] * a + 255 * (1 - a));
    out[i + 1] = Math.round(src.data[i + 1] * a + 255 * (1 - a));
    out[i + 2] = Math.round(src.data[i + 2] * a + 255 * (1 - a));
    out[i + 3] = 255;
  }
  return { width: src.width, height: src.height, data: out };
}

/**
 * jpeg-js kodlayıcısı sonucu Node'un `Buffer`ı ile döndürüyor ve telefonun
 * JavaScript motorunda `Buffer` yok. Kodlama eşzamanlı olduğu için yalnız o
 * çağrı süresince, `Uint8Array` üreten küçük bir yerine koyma tanımlanıyor;
 * arada başka bir kod çalışamaz.
 */
function withByteBuffer<T>(run: () => T): T {
  const g = globalThis as { Buffer?: unknown };
  if (typeof g.Buffer !== 'undefined') return run();
  g.Buffer = { from: (bytes: ArrayLike<number>) => Uint8Array.from(bytes) };
  try {
    return run();
  } finally {
    delete g.Buffer;
  }
}

export function encodeJpeg(src: Rgba, quality: number): Uint8Array {
  const flat = flattenOnWhite(src);
  const out = withByteBuffer(() => encodeJpegRaw(flat, quality).data);
  return out instanceof Uint8Array ? new Uint8Array(out) : Uint8Array.from(out as ArrayLike<number>);
}

export function encodePng(src: Rgba): Uint8Array {
  return encodePngRaw(src);
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function toBase64(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
  }
  if (i < bytes.length) {
    const n = (bytes[i] << 16) | ((i + 1 < bytes.length ? bytes[i + 1] : 0) << 8);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
    out += i + 1 < bytes.length ? B64[(n >> 6) & 63] : '=';
    out += '=';
  }
  return out;
}

export const CARD_IMAGE = { width: 480, height: 252, maxBytes: 22_000 } as const;
export const ICON = { size: 64, keepAsIsBytes: 6_000, maxBytes: 8_000 } as const;

/**
 * Büyük kartın görseli: 480×252 (paylaşım görsellerinin 1.91:1 oranı),
 * ortasından kırpılmış JPEG. Sığmazsa önce kalite, sonra boyut düşer.
 */
export function makeCardImage(bytes: Uint8Array): string | null {
  const src = decodeImage(bytes);
  if (!src || src.width < 32 || src.height < 32) return null;
  for (const [width, height] of [[CARD_IMAGE.width, CARD_IMAGE.height], [360, 189]] as const) {
    const resized = coverResize(src, width, height);
    for (const quality of [62, 48, 36]) {
      const jpeg = encodeJpeg(resized, quality);
      if (jpeg.length <= CARD_IMAGE.maxBytes) return `data:image/jpeg;base64,${toBase64(jpeg)}`;
    }
  }
  return null;
}

/**
 * Sitenin logosu. Zaten küçük bir PNG ise olduğu gibi — saydamlığı korunur;
 * değilse 64×64 PNG'ye küçültülür.
 */
export function makeIcon(bytes: Uint8Array): string | null {
  const kind = sniffImage(bytes);
  const png = kind === 'ico' ? pngFromIco(bytes) : kind === 'png' ? bytes : null;
  if (png && png.length <= ICON.keepAsIsBytes) return `data:image/png;base64,${toBase64(png)}`;
  const src = decodeImage(bytes);
  if (!src) return null;
  const side = Math.min(ICON.size, src.width, src.height);
  const resized = coverResize(src, side, side);
  const out = encodePng(resized);
  if (out.length <= ICON.maxBytes) return `data:image/png;base64,${toBase64(out)}`;
  const jpeg = encodeJpeg(resized, 80);
  return jpeg.length <= ICON.maxBytes ? `data:image/jpeg;base64,${toBase64(jpeg)}` : null;
}
